const BLOCKED_TRACKING_HOSTS = ["localhost", "127.0.0.1", "staging.inciteai.com"];
const GOOGLE_ADS_CONVERSION_ID = "11333696455";
const GOOGLE_ADS_SIGN_UP_LABEL = "CPI2CKOAj5wcEMf3qZwq";
const GOOGLE_ADS_PURCHASE_LABEL = "kTJqCKCAj5wcEMf3qZwq";
const X_SIGN_UP_EVENT_ID = "tw-ohy5t-rc4q7";
const BASE_ATTRIBUTION_PARAM_NAMES = [
  "twclid",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const ATTRIBUTION_PARAM_PREFIXES = ["utm_", "ic_"];
// Added by inciteai.com to its app links; they name a button, not a source.
const INTERNAL_NAVIGATION_PARAMS = ["ic_cta", "ic_route"];
const ATTRIBUTION_CAPTURE_SESSION_KEY = "incite_attrib_capture";
const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

const isBlockedTrackingHost = () => {
  if (typeof window === "undefined") {
    return true;
  }

  return BLOCKED_TRACKING_HOSTS.includes(window.location.hostname);
};

const dataLayerAvailable = () =>
  typeof window !== "undefined" && Array.isArray(window.dataLayer);

const facebookPixelAvailable = () =>
  typeof window !== "undefined" && typeof window.fbq === "function";

const googleAdsAvailable = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

const xPixelAvailable = () =>
  typeof window !== "undefined" && typeof window.twq === "function";

const trackedEventStorageKey = (dedupeKey) => `incite_tracked_event:${dedupeKey}`;
const attributionStorageKey = (touch) => `incite_attrib:${touch}`;

const filteredAttributionParams = (search) => {
  const params = new URLSearchParams(search || "");
  const out = {};

  params.forEach((value, key) => {
    const normalizedKey = key.toLowerCase();
    const shouldCapture =
      BASE_ATTRIBUTION_PARAM_NAMES.includes(normalizedKey) ||
      ATTRIBUTION_PARAM_PREFIXES.some((prefix) => normalizedKey.startsWith(prefix));

    if (shouldCapture && value && !out[normalizedKey]) {
      out[normalizedKey] = value;
    }
  });

  return out;
};

const hasAttributionParams = (params) => Object.keys(params || {}).length > 0;

const cookieDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === "inciteai.com" || hostname.endsWith(".inciteai.com")) {
    return "Domain=.inciteai.com";
  }
  return null;
};

const writeAttributionCookie = (name, value) => {
  try {
    const parts = [
      `${name}=${encodeURIComponent(JSON.stringify(value))}`,
      "Path=/",
      `Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}`,
      "SameSite=Lax",
    ];
    if (window.location.protocol === "https:") {
      parts.push("Secure");
    }
    const domain = cookieDomain();
    if (domain) {
      parts.push(domain);
    }
    document.cookie = parts.join("; ");
  } catch (_error) {
    return;
  }
};

const readStoredAttribution = (touch = "last_touch") => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(attributionStorageKey(touch));
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const writeStoredAttribution = (touch, payload) => {
  try {
    window.localStorage.setItem(attributionStorageKey(touch), JSON.stringify(payload));
  } catch (_error) {
    return;
  }
};

const isInciteHost = (hostname) =>
  hostname === "inciteai.com" || hostname.endsWith(".inciteai.com");

// The page that sent this visitor, such as a search engine, a social post or
// another site. Our own pages (inciteai.com links open with no referrer) are
// never a source.
const externalReferrer = () => {
  const referrer = document.referrer;
  if (!referrer) {
    return null;
  }

  try {
    const { hostname } = new URL(referrer);
    if (!hostname || isInciteHost(hostname) || hostname === window.location.hostname) {
      return null;
    }
  } catch (_error) {
    return null;
  }

  return referrer.slice(0, 1000);
};

const readAttributionCookie = (name) => {
  try {
    const entry = document.cookie
      .split(";")
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(`${name}=`));
    if (!entry) {
      return null;
    }

    const parsed = JSON.parse(decodeURIComponent(entry.slice(name.length + 1)));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_error) {
    return null;
  }
};

// Every tracked event calls captureAttributionFromUrl, and a single-page app
// keeps the same URL and referrer, so record each landing only once per tab.
const alreadyCapturedThisSession = (captureKey) => {
  try {
    if (window.sessionStorage.getItem(ATTRIBUTION_CAPTURE_SESSION_KEY) === captureKey) {
      return true;
    }
    window.sessionStorage.setItem(ATTRIBUTION_CAPTURE_SESSION_KEY, captureKey);
  } catch (_error) {
    return false;
  }
  return false;
};

export const captureAttributionFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  // inciteai.com records the visitor's real first touch in a cookie shared with
  // this app. Adopt it instead of replacing it with the website hop.
  const sharedFirstTouch = readAttributionCookie("incite_attrib_ft");
  if (sharedFirstTouch && !readStoredAttribution("first_touch")) {
    writeStoredAttribution("first_touch", sharedFirstTouch);
  }

  const params = filteredAttributionParams(window.location.search);
  const referrer = externalReferrer();
  if (!hasAttributionParams(params) && !referrer) {
    return readStoredAttribution();
  }

  if (alreadyCapturedThisSession(`${window.location.search}|${referrer || ""}`)) {
    return readStoredAttribution();
  }

  // Website links only say which button was used. That hop is not a new
  // source, so keep the visitor's existing last touch and add the button.
  const websiteHop =
    !referrer &&
    Object.keys(params).every((key) => INTERNAL_NAVIGATION_PARAMS.includes(key));
  const existingLastTouch = websiteHop
    ? readAttributionCookie("incite_attrib_lt") || readStoredAttribution()
    : null;
  if (existingLastTouch) {
    const merged = {
      ...existingLastTouch,
      params: { ...(existingLastTouch.params || {}), ...params },
    };
    writeStoredAttribution("last_touch", merged);
    writeAttributionCookie("incite_attrib_lt", merged);
    return merged;
  }

  const payload = {
    params,
    landing_path: `${window.location.pathname}${window.location.search}`,
    landing_url: window.location.href.slice(0, 1000),
    referrer: referrer || (document.referrer ? document.referrer.slice(0, 1000) : undefined),
    captured_at: new Date().toISOString(),
  };

  if (!readStoredAttribution("first_touch") && !sharedFirstTouch) {
    writeStoredAttribution("first_touch", payload);
    writeAttributionCookie("incite_attrib_ft", payload);
  }

  writeStoredAttribution("last_touch", payload);
  writeAttributionCookie("incite_attrib_lt", payload);

  return payload;
};

const storedTwclid = () =>
  readStoredAttribution("last_touch")?.params?.twclid ||
  readStoredAttribution("first_touch")?.params?.twclid;

export const hasTrackedEvent = (dedupeKey) => {
  if (!dedupeKey || typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(trackedEventStorageKey(dedupeKey)) === "1";
  } catch (_) {
    return false;
  }
};

const markTrackedEvent = (dedupeKey) => {
  if (!dedupeKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(trackedEventStorageKey(dedupeKey), "1");
  } catch (_error) {
    return;
  }
};

const normalizeTrackingEvents = (rawEvents) => {
  if (!rawEvents) {
    return [];
  }

  return Array.isArray(rawEvents) ? rawEvents : [rawEvents];
};

const sendToValue = (label) => `AW-${GOOGLE_ADS_CONVERSION_ID}/${label}`;

const trackGoogleAdsConversion = (payload) => {
  if (!googleAdsAvailable()) {
    return false;
  }

  if (payload.event === "sign_up") {
    window.gtag("event", "conversion", {
      send_to: sendToValue(GOOGLE_ADS_SIGN_UP_LABEL),
    });
    return true;
  }

  if (payload.event === "purchase") {
    window.gtag("event", "conversion", {
      send_to: sendToValue(GOOGLE_ADS_PURCHASE_LABEL),
      value: payload.value,
      currency: payload.currency,
      transaction_id: payload.transaction_id,
      new_customer: true,
    });
    return true;
  }

  return false;
};

const trackXConversion = (payload, dedupeKey) => {
  if (!xPixelAvailable()) {
    return false;
  }

  if (payload.event === "sign_up") {
    const params = {
      status: "completed",
    };
    if (dedupeKey) {
      params.conversion_id = dedupeKey;
    }
    const twclid = payload.twclid || storedTwclid();
    if (twclid) {
      params.twclid = twclid;
    }

    window.twq("event", X_SIGN_UP_EVENT_ID, params);
    return true;
  }

  return false;
};

export const trackBackendEvents = (rawEvents) => {
  if (isBlockedTrackingHost()) {
    return;
  }

  captureAttributionFromUrl();

  const events = normalizeTrackingEvents(rawEvents);

  events.forEach((eventPayload) => {
    if (!eventPayload?.event) {
      return;
    }

    const dedupeKey = eventPayload.dedupe_key;
    if (dedupeKey && hasTrackedEvent(dedupeKey)) {
      return;
    }

    const payload = { ...eventPayload };
    delete payload.dedupe_key;

    if (payload.ecommerce && dataLayerAvailable()) {
      window.dataLayer.push({ ecommerce: null });
    }

    if (dataLayerAvailable()) {
      window.dataLayer.push(payload);
    }

    if (payload.event === "sign_up" && facebookPixelAvailable()) {
      window.fbq("track", "SignUp");
    }

    const googleAdsTracked = trackGoogleAdsConversion(payload);
    const xTracked = trackXConversion(payload, dedupeKey);

    if (dedupeKey && (dataLayerAvailable() || facebookPixelAvailable() || googleAdsTracked || xTracked)) {
      markTrackedEvent(dedupeKey);
    }
  });
};

export const trackFrontendEvent = (event, metadata = {}) => {
  if (!event || isBlockedTrackingHost()) {
    return;
  }

  captureAttributionFromUrl();

  if (dataLayerAvailable()) {
    window.dataLayer.push({
      event,
      ...metadata,
    });
  }
};
